import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import * as yup from 'yup';

import { REQUIRED_TEXT } from '@/common/constants';
import { PlanFundingMethodType } from '@/modules/employer/components/Contributions/contributions.types';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';

import { useAccountFundingParamsStore } from '../stores';

import { OMNIBUS_TYPES } from './useAccountFundingParamsFields';

export enum EOYReconciliationOptions {
  BANK_ACCOUNT = "BANK_ACCOUNT",
  EMPLOYER = "EMPLOYER",
  PARTNER = "PARTNER",
  NONE = "NONE",
}

const useEOYPlanReconciliationFields = (
  isEditMode?: boolean,
  fundingMethod?: string,
  omnibus?: string
) => {
  const isBusinessLogicDisabled = useMemo(
    () => omnibus === OMNIBUS_TYPES.HSA,
    [omnibus]
  );

  const eoyPlanReconciliation = useAccountFundingParamsStore((state) => state.state.eoyPlanReconciliation);

  const eoyDefaultValue = useMemo(
    () => (fundingMethod === PlanFundingMethodType.STANDARD_NOTIONAL
      && (!eoyPlanReconciliation || eoyPlanReconciliation === EOYReconciliationOptions.NONE)
      ? EOYReconciliationOptions.BANK_ACCOUNT
      : undefined),
    [fundingMethod, eoyPlanReconciliation]
  );

  const fields = useMemo(() => {
    const allFields = [
      {
        name: PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION,
        type: FieldTypes.Radio,
        disabled: !isEditMode,
        label: (
          <Box flex={{ grow: 1 }}>
            <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
              EOY plan reconciliation
            </Inscription>
            <Box>
              <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
                Defines where to send money at the end of the year
              </Inscription>
            </Box>
          </Box>
        ),
        options: [
          { label: 'Bank Account', value: EOYReconciliationOptions.BANK_ACCOUNT },
          { label: 'Employer', value: EOYReconciliationOptions.EMPLOYER },
          { label: 'Partner', value: EOYReconciliationOptions.PARTNER },
        ],
        placeholder: 'Select option',
        showRequireIcon: true,
        value: eoyDefaultValue,
        validator: yup.string().required(REQUIRED_TEXT).test({
          test: (val) => val !== EOYReconciliationOptions.NONE,
          message: REQUIRED_TEXT,
        }),
      },
      {
        name: PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION_DELAY_DAYS,
        type: FieldTypes.Number,
        disabled: !isEditMode,
        placeholder: 'Delay days',
        showRequireIcon: true,
        label: (
          <Box flex={{ grow: 1 }}>
            <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
              Delay days
            </Inscription>
            <Box>
              <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
                Number of days at the end of the year before this process starts
              </Inscription>
            </Box>
          </Box>
        ),
        validator: yup.string().required(REQUIRED_TEXT).test('max-365', 'Maximum value is 365 days', (val) => {
          const num = Number(val);
          return !isNaN(num) && num <= 365;
        }),
      }
    ] as Field[];

    return isBusinessLogicDisabled ? [] : allFields;
  }, [isEditMode, isBusinessLogicDisabled, eoyDefaultValue]);

  return fields;
};

export default useEOYPlanReconciliationFields;
